import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder } from '@angular/forms';
import { StorageService } from '@morningstar/shared/base/storage-service';
import { SyncfusionChartSettingsHelper, YAxisHelper } from '@morningstar/shared/util';
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import { delay, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PeriodContext } from '../../../shared/periods/period-contexts';
import { PeriodService } from '../../../shared/periods/period.service';
import { DashboardBaseComponent } from '../../dashboard-base/dashboard-base.component';
import { RevenueByCategoryService } from '../revenue-by-category.service';

@Component({
  selector: 'wsg-two-month-compare',
  templateUrl: './two-month-compare.component.html',
  styleUrls: ['./two-month-compare.component.scss']
})
export class TwoMonthCompareComponent extends DashboardBaseComponent implements AfterViewInit, OnDestroy {

  title: string = 'Two Month Compare';
  chartId = 'twoMonthCompareChart';
  initialChartSettings = SyncfusionChartSettingsHelper.getCurrencyBarChartSettings();
  context = PeriodContext.invoice;

  // == STATE ===========================================================

  chartSettings = new BehaviorSubject<any>(SyncfusionChartSettingsHelper.getCurrencyBarChartSettings());
  chartSettings$ = this.chartSettings.asObservable();

  form = this.formBuilder.group({
    location: 'ALL',
    firstPeriod: null,
    secondPeriod: null
  });

  periods$ = this.periodService.getInvoicePeriodsObservable();

  // == ACTIONS =========================================================

  constructorLoaded = new Subject();
  constructorLoaded$ = this.constructorLoaded.asObservable();

  // == INTERMEDIATE ====================================================

  location$ = this.form.get('location').valueChanges.pipe(startWith('ALL'));
  firstPeriod$ = this.form.get('firstPeriod').valueChanges;
  secondPeriod$ = this.form.get('secondPeriod').valueChanges;

  twoMonthCompare$ = combineLatest([this.location$, this.firstPeriod$, this.secondPeriod$]).pipe(
    filter(([location, firstPeriod, secondPeriod]) => !!location && !!firstPeriod && !!secondPeriod),
    switchMap(([location, firstPeriod, secondPeriod]) => this.revenueByCategoryService.getTwoMonthCompare(location, firstPeriod, secondPeriod))
  );

  formattedChartSettings$ = this.twoMonthCompare$.pipe(
    withLatestFrom(this.chartSettings$),
    map(([twoMonthCompare, chartSettings]) => {
      if (twoMonthCompare && twoMonthCompare.length > 0) {
        const maxValueFromArray = Math.max(...twoMonthCompare[0].categories.map(r => r.amount), ...twoMonthCompare[1].categories.map(r => r.amount), 0);
        const minValueFromArray = Math.min(...twoMonthCompare[0].categories.map(r => r.amount), ...twoMonthCompare[1].categories.map(r => r.amount));

        let nearestOrderOfMagnitude = YAxisHelper.getNearestOrderOfMagnitdue(maxValueFromArray);

        if (nearestOrderOfMagnitude === 0) {
          nearestOrderOfMagnitude = 1;
        }

        const chartMax = Math.ceil(maxValueFromArray / nearestOrderOfMagnitude) * nearestOrderOfMagnitude;
        const chartMin = Math.floor(minValueFromArray / nearestOrderOfMagnitude) * nearestOrderOfMagnitude;

        chartSettings.primaryYAxis['maximum'] = chartMax;
        chartSettings.primaryYAxis['minimum'] = chartMin;
        chartSettings.primaryYAxis['interval'] = Math.round(Math.ceil((chartMax - chartMin) / 5));
        chartSettings.tooltip = chartSettings.tooltip;
      }

      return chartSettings;
    })
  );

  // == SIDE EFFECTS ====================================================

  onTwoMonthCompare$ = this.formattedChartSettings$.pipe(
    filter(([_, __]) => !!this.chart),
    withLatestFrom(this.twoMonthCompare$),
    tap(([chartSettings, revenue]) => {
      this.chart.primaryYAxis = chartSettings.primaryYAxis;
      this.chart.primaryXAxis = chartSettings.primaryXAxis;
      this.chart.chartArea = chartSettings.chartArea;

      this.chart.dataSource = revenue;
      this.chart.refresh();
    })
  );

  onPeriods$ = this.periods$.pipe(
    tap(periods => {      
      this.secondPeriod.setValue(periods[periods.length - 1]);        

      if (periods.length > 1) {
        this.firstPeriod.setValue(periods[periods.length - 2]);
      } else {
        this.firstPeriod.setValue(periods[periods.length - 1]);        
      }
    })
  );

  // == SUBSCRIPTION ====================================================

  subscription = merge(
    this.onTwoMonthCompare$,
    this.onPeriods$
  ).subscribe();

  // == LIFECYCLE =======================================================

  constructor(private revenueByCategoryService: RevenueByCategoryService,
              private formBuilder: FormBuilder,
              private periodService: PeriodService,
              protected storageService: StorageService) {
    super(storageService);

    this.constructorLoaded.next();
  }

  ngAfterViewInit() {
    this.subscription.add(this.theme$.pipe(
      delay(50),
      filter(_ => !!this.chart),
      tap(_ => this.chart.refresh())
    ).subscribe())
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // == HELPERS ========================================================

  get firstPeriod(): AbstractControl {
    return this.form.get('firstPeriod');
  }

  get secondPeriod(): AbstractControl {
    return this.form.get('secondPeriod');
  }
}
